import { useEffect, useRef, useState } from 'react';

import {
  Step,
  TitleMap,
} from '../../../../../../components/AuthModal/AuthModal.types';
import { ForgottenPasswordForm } from '../../../../../../components/AuthModal/components/ForgottenPasswordForm/ForgottenPasswordForm';
import { LogInForm } from '../../../../../../components/AuthModal/components/LogInForm/LogInForm';
import { RegisterForm } from '../../../../../../components/AuthModal/components/RegisterForm/RegisterForm';
import { Overlay } from '../../../../../../components/Overlay/Overlay';
import { useTranslate } from '../../../../../../hooks/useTranslate';
import type { SembonusUserInfo } from '../../../../../../providers/UserProvider';
import {
  Body,
  Container,
  Header,
  StyledCloseButton,
  Title,
} from './LegacyAuthModal.styles';

interface Props {
  onSignedUp: ({ optedInToSembonus }: { optedInToSembonus: boolean }) => void;
  onLoggedIn: (user: SembonusUserInfo | undefined) => void;
  onClose: () => void;
}

export const LegacyAuthModal = ({ onSignedUp, onLoggedIn, onClose }: Props) => {
  const { t } = useTranslate();
  const [step, setStep] = useState<Step>('log-in');
  const loginFormRef = useRef<HTMLDivElement | null>(null);
  const [loginDirty, setLoginDirty] = useState(false);

  useEffect(() => {
    if (step === 'log-in' && loginFormRef.current) {
      const firstInput = loginFormRef.current.querySelector('input');
      if (firstInput) {
        firstInput.focus();
      }
    }
  }, [step]);

  const showForgottenPassword = () => setStep('forgotten-password');
  const showRegister = () => setStep('register');
  const showLogIn = () => setStep('log-in');

  const titleMap: TitleMap = {
    'log-in': t('ui.action.logIn.header'),
    register: t('ui.action.register.header'),
    'forgotten-password': t('ui.action.forgottenPassword.header'),
  };

  return (
    <Overlay
      isOpen
      zIndex={10000} // share widget has 9999, so this should be higher
      onClick={(e) => {
        if (loginDirty) {
          return;
        }

        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Container>
        <Header>
          <Title>{titleMap[step]}</Title>
          <StyledCloseButton onClick={onClose} />
        </Header>
        <Body ref={step === 'log-in' ? loginFormRef : null}>
          {step === 'log-in' && (
            <LogInForm
              onSuccess={onLoggedIn}
              onRegister={showRegister}
              onForgottenPassword={showForgottenPassword}
            />
          )}
          {step === 'register' && (
            <RegisterForm onSuccess={onSignedUp} onLogIn={showLogIn} />
          )}
          {step === 'forgotten-password' && (
            <ForgottenPasswordForm onCancel={showLogIn} />
          )}
        </Body>
      </Container>
    </Overlay>
  );
};
